import { FC, useRef } from 'react';

import { useTranslation } from 'libs/i18n';
import { useDialog } from 'libs/ui/Dialog/useDialog';
import {
  DialogDeleteAndReasonChange,
  DialogReasonChange,
  ClientFormContent,
} from 'modules/client/common/components';
import {
  ActionProps,
  ActionType,
} from 'modules/client/common/components/ClientFormContent/types';
import {
  LockAndUnlockReadOnlyProps,
  useLockAndUnlockReadOnly,
} from 'modules/client/common/hooks';

import { useFormContext } from '../../../forms/legalClientBranchForm';
import {
  LegalClientBranchSections,
  LegalClientBranchSectionsProps,
} from '../../LegalClientBranchSections';

export interface FormContentProps
  extends LockAndUnlockReadOnlyProps,
    LegalClientBranchSectionsProps {
  submitLoading?: boolean;
  deleteLoading?: boolean;
  dearchiveLoading?: boolean;
  archiveLoading?: boolean;
  onShowVersionHistory?: () => void;
  onDelete?: (reason: string) => void;
  onArchive?: (reason: string) => Promise<boolean>;
  onDearchive?: (reason: string) => Promise<boolean>;
}

export const FormContent: FC<FormContentProps> = ({
  submitLoading,
  deleteLoading,
  archiveLoading,
  dearchiveLoading,
  lockClientIfPossible,
  unlockClient,
  onShowVersionHistory,
  onDelete,
  onArchive,
  onDearchive,
  ...sectionsProps
}) => {
  const { t } = useTranslation();
  const { resetForm, initialValues, setReadOnly } = useFormContext();

  const { onDisableReadOnly, onEnableReadOnly } = useLockAndUnlockReadOnly(
    setReadOnly,
    { unlockClient, lockClientIfPossible }
  );
  const { open: openDeleteDialog, ...deleteDialogProps } = useDialog();
  const { open: openDialogReason, ...dialogReasonProps } = useDialog();

  const actionRef = useRef<ActionType>();

  const { fullName } = initialValues.branchName;

  const headerTitle = fullName
    ? t('client.legal.legalClientBranch.title', {
        name: fullName,
      })
    : '';

  const onActionArchive = () => {
    actionRef.current = ActionType.archive;
    openDialogReason();
  };

  const onActionDearchive = () => {
    actionRef.current = ActionType.dearchive;
    openDialogReason();
  };

  const onSaveReason = async (reason: string) => {
    if (actionRef.current === ActionType.archive) {
      if (await onArchive?.(reason)) {
        dialogReasonProps.onClose();
      }
    } else if (actionRef.current === ActionType.dearchive) {
      if (await onDearchive?.(reason)) {
        dialogReasonProps.onClose();
      }
    }
  };

  const actionMenuProps: ActionProps = {
    showEditAction: !onDearchive,
    onDelete: onDelete ? openDeleteDialog : undefined,
    onArchive: onArchive ? onActionArchive : undefined,
    onDearchive: onDearchive ? onActionDearchive : undefined,
  };

  return (
    <>
      <ClientFormContent
        actionMenuProps={actionMenuProps}
        content={<LegalClientBranchSections {...sectionsProps} />}
        headerTitle={headerTitle}
        resetForm={resetForm}
        shownTabBar={false}
        submitLoading={submitLoading}
        onEdit={onDisableReadOnly}
        onEnableReadOnly={onEnableReadOnly}
        onShowVersionHistory={onShowVersionHistory}
      />
      {onDelete && (
        <DialogDeleteAndReasonChange
          {...deleteDialogProps}
          deleteLoading={deleteLoading}
          onDelete={onDelete}
        />
      )}

      <DialogReasonChange
        submitLoading={archiveLoading ?? dearchiveLoading}
        onSave={onSaveReason}
        {...dialogReasonProps}
      />
    </>
  );
};
